import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, Loader2, MapPin, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const ST = {
  accepted: ["A accepté", "bg-[#D6F5D6]"],
  offer: ["A proposé un prix", "bg-[#FFF3CC]"],
  declined: ["A décliné", "bg-[#FFEAE5]"],
  cancelled: ["A annulé", "bg-[#FFEAE5]"],
  no_reply: ["Sans réponse", "bg-secondary/60"],
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { timeZone: "Europe/Paris", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");

const EVT = "admin-notified-drivers-open";

// Bouton : ouvre la fenêtre (hébergée par NotifiedDriversHost, stable aux re-rendus des listes).
export default function NotifiedDriversButton({ requestId }) {
  return (
    <button type="button" onClick={() => window.dispatchEvent(new CustomEvent(EVT, { detail: requestId }))} data-testid={`admin-ride-notified-${requestId}`}
      className="street-press mt-2 inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-[10px] font-black uppercase tracking-widest street-border-thin">
      <Bell className="h-3 w-3" />Conducteurs notifiés
    </button>
  );
}

export function NotifiedDriversHost() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onOpen = async (e) => {
      setOpen(true);
      setData(null);
      try { setData((await api.get(`/admin/price/rides/${e.detail}/notified-drivers`)).data); } catch (err) { toast.error(formatApiError(err)); setOpen(false); }
    };
    window.addEventListener(EVT, onOpen);
    return () => window.removeEventListener(EVT, onOpen);
  }, []);
  if (!open) return null;
  return (
    <>
        <div className="fixed inset-0 z-[200] flex items-end justify-center bg-black/50 sm:items-center" onClick={() => setOpen(false)} data-testid="notified-drivers-dialog">
          <div className="max-h-[85dvh] w-full overflow-y-auto rounded-t-3xl bg-[#FFF8F1] p-4 street-border sm:max-w-lg sm:rounded-3xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="font-[Outfit] text-[17px] font-black">Conducteurs notifiés</div>
              <button type="button" onClick={() => setOpen(false)} className="rounded-full bg-white p-2 street-border-thin" data-testid="notified-drivers-close"><X className="h-4 w-4" /></button>
            </div>
            {!data ? <Loader2 className="mt-4 h-5 w-5 animate-spin" /> : (
              <>
                <p className="mt-1 text-[12px] font-semibold text-foreground/60" data-testid="notified-drivers-count">
                  {data.count} conducteur{data.count > 1 ? "s" : ""} · {data.broadcasts} envoi{data.broadcasts > 1 ? "s" : ""}
                </p>
                {data.items.length === 0 && <p className="mt-3 rounded-xl bg-white p-3 text-[12px] font-semibold street-border-thin" data-testid="notified-drivers-empty">Aucun conducteur n&apos;a été notifié (ou demande antérieure au suivi).</p>}
                <div className="mt-3 space-y-2">
                  {data.items.map((d) => (
                    <div key={d.driver_user_id} className="rounded-2xl bg-white p-3 street-border-thin" data-testid={`notified-driver-${d.driver_user_id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="truncate text-[13px] font-black">{d.name}</div>
                          <div className="text-[11px] font-semibold text-foreground/60">{[d.phone, d.city, d.vehicle].filter(Boolean).join(" · ")}</div>
                        </div>
                        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-black street-border-thin ${ST[d.status][1]}`} data-testid={`notified-driver-status-${d.driver_user_id}`}>
                          {ST[d.status][0]}{d.offer_amount ? ` · ${Number(d.offer_amount).toFixed(2)}€` : ""}
                        </span>
                      </div>
                      <div className="mt-1 text-[11px] font-semibold text-foreground/60">
                        Notifié le {fmt(d.first_at)}{d.times > 1 ? ` · ${d.times} fois (dernier ${fmt(d.last_at)})` : ""}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-1.5 text-[11px] font-black">
                        <span className="inline-flex items-center gap-1 rounded-full bg-secondary/60 px-2 py-0.5" data-testid={`notified-driver-distance-${d.driver_user_id}`}>
                          <MapPin className="h-3 w-3" />{d.distance_km != null ? `${Number(d.distance_km).toLocaleString("fr-FR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} km du départ` : "Distance inconnue"}
                        </span>
                        {d.eta_min != null && <span className="rounded-full bg-secondary/60 px-2 py-0.5" data-testid={`notified-driver-eta-${d.driver_user_id}`}>{d.eta_min} min</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
    </>
  );
}
